import type { JSX } from 'react';
import { Divider } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { Process, ProcessActorProfession } from '@common/interfaces';
import type { Override } from '@common/utils/utilityTypes';
import { CustomControlCode, ProcessActivityStatus, Profession, ServiceCode } from '@common/enums';
import {
    findLatestProcessByServiceCode,
    findSingleProcessByServiceCode,
    hasActivityWithStatus,
    hasCompletedActivity,
} from '@common/utils/processUtil';
import { getProcessAuthorizations } from '@frontend/services/api/processesApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import type { EditOption } from '../careProvidersTeam/CareProvidersTeamEditModal';
import { CareProvidersTeam } from '../careProvidersTeam/CareProvidersTeam';

type BariatricsService = Override<Process['service'], { code: ServiceCode.BariPost | ServiceCode.BariPre }>;

type BariatricsProcess = Override<Process, { service: BariatricsService }>;
const processActorProfessionOrder = {
    [Profession.lifestyleCoach]: 0,
    [Profession.dietitian]: 1,
    [Profession.basicPsychologist]: 2,
    [Profession.outpatientAssistant]: 3,
    [Profession.bariNurse]: 4,
};

const careProvidersTeamEditOptions: EditOption[] = [
    {
        isEditable: false,
        profession: Profession.bariNurse,
    },
    {
        alertText: 'Het wijzigen van deze organisatie heeft gevolgen voor de planning, houd hier rekening mee.',
        isEditable: false,
        profession: Profession.dietitian,
    },
    {
        alertText:
            'Het wijzigen van deze organisatie heeft gevolgen voor de afgesproken verdeling, doe dit alleen als het echt nodig is.',
        isEditable: false,
        profession: Profession.lifestyleCoach,
    },
    {
        alertText: 'Het wijzigen van deze organisatie heeft gevolgen voor de planning, houd hier rekening mee.',
        isEditable: false,
        profession: Profession.basicPsychologist,
    },
];

export function BariatricsSidebar(): JSX.Element | null {
    const abortSignal = useAbortSignal();
    const { processes } = usePatientEpisodeCategoryContext();
    const [canReadProcessActors, setCanReadProcessActors] = useState(false);
    const [updatableProcessActors, setUpdatableProcessActors] = useState<ProcessActorProfession[]>([]);
    const [editOptions, setEditOptions] = useState<EditOption[]>(careProvidersTeamEditOptions);
    const [process, setProcess] = useState<Process>();

    useEffect(() => {
        loadProcessAuthorizations();
    }, [processes]);

    useEffect(() => {
        if (updatableProcessActors.length > 0 && process) {
            loadEditOptions();
        }
    }, [updatableProcessActors, process]);

    if (!process || !canReadProcessActors) {
        return null;
    }

    return (
        <>
            <Divider />
            <CareProvidersTeam
                editOptions={editOptions}
                editTitle={getEditCareProvidersTeamTitle()}
                isEditable={getCareProvidersTeamIsEditable()}
                process={process}
                processActorProfessionOrder={processActorProfessionOrder}
                title={process.service.name}
            />
        </>
    );

    function getEditCareProvidersTeamTitle(): string {
        const split = process?.service.name.split(' ') ?? [];
        const suffix = split.length > 1 ? split[1] : '';

        return `${getSelectedLanguage().patient.editCareProvidersTeam} ${suffix}`;
    }

    function findProcess(): BariatricsProcess | undefined {
        const bariPostProcess = findSingleProcessByServiceCode(processes, ServiceCode.BariPost);
        if (
            bariPostProcess &&
            (hasActivityWithStatus(bariPostProcess, CustomControlCode.BariPostSurgeryDate, [
                ProcessActivityStatus.complete,
                ProcessActivityStatus.cancelled,
            ]) ||
                hasCompletedActivity(bariPostProcess, CustomControlCode.BariPostEnrollment))
        ) {
            return bariPostProcess as BariatricsProcess;
        }

        return findLatestProcessByServiceCode(processes, ServiceCode.BariPre) as BariatricsProcess | undefined;
    }

    async function loadProcessAuthorizations(): Promise<void> {
        const nextProcess = findProcess();
        if (!nextProcess) {
            return setProcess(undefined);
        }
        setProcess(nextProcess);

        const authorizations = await getProcessAuthorizations(nextProcess.id, abortSignal);
        const serviceAuthorization = authorizations?.serviceAuthorizations?.[nextProcess.service.code];
        setCanReadProcessActors(serviceAuthorization?.canReadProcessActors ?? false);
        setUpdatableProcessActors(serviceAuthorization?.updatableProcessActors ?? []);
    }

    function getCareProvidersTeamIsEditable(): boolean {
        if (
            process?.service.code === ServiceCode.BariPost &&
            hasCompletedActivity(process, CustomControlCode.BariPostDietConsultReport)
        ) {
            return false;
        }

        return updatableProcessActors.length > 0;
    }

    function loadEditOptions(): void {
        const nextEditOptions = editOptions.map((editOption) => ({
            ...editOption,
            isEditable: getCareProvidersTeamIsEditable() && updatableProcessActors.includes(editOption.profession),
        }));
        setEditOptions(nextEditOptions);
    }
}
